import React, { useState, useEffect } from 'react';
import { getAllSupportEmails } from '../firebase/emailsupports';
import DialogReplyEmail from '../components/dialogs/DialogReplyEmail';
import DialogViewReply from '../components/dialogs/DialogViewReply';
import Pagination from '../components/commons/Pagination';

const EmailSupportManagement = () => {
    const [emails, setEmails] = useState([]);
    const [filteredEmails, setFilteredEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'replied', 'not_replied'
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [showReplyDialog, setShowReplyDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchEmails();
    }, []);

    useEffect(() => {
        filterEmails();
    }, [emails, filter]);

    const fetchEmails = async () => {
        setLoading(true);
        try {
            const data = await getAllSupportEmails();
            setEmails(data);
        } catch (error) {
            console.error('Error fetching emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterEmails = () => {
        let filtered = emails;
        if (filter === 'replied') {
            filtered = emails.filter(email => email.replied);
        } else if (filter === 'not_replied') {
            filtered = emails.filter(email => !email.replied);
        }
        setFilteredEmails(filtered);
        setCurrentPage(1);
    };

    const handleReply = (email) => {
        setSelectedEmail(email);
        setShowReplyDialog(true);
    };

    const handleView = (email) => {
        setSelectedEmail(email);
        setShowViewDialog(true);
    };

    const handleReplySent = () => {
        fetchEmails(); // Refresh the list
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const paginatedEmails = filteredEmails.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    if (loading) {
        return (
            <div className="min-h-full bg-[#0F0F0F] text-white">
                <div className="px-10 py-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading support emails...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-full bg-[#0F0F0F] text-white">
            <div className="px-10 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-primary text-[36px]">Email Support Manager</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="px-6 py-2 bg-[#1F1F1F] rounded-[33px] text-[#666] text-center">
                            {filteredEmails.length} Support Emails
                        </div>
                    </div>
                </div>

                {/* Filter */}
                <div className="mb-6 flex space-x-4">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-primary text-white' : 'bg-gray-600 text-white'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilter('not_replied')}
                        className={`px-4 py-2 rounded ${filter === 'not_replied' ? 'bg-primary text-white' : 'bg-gray-600 text-white'}`}
                    >
                        Not Replied
                    </button>
                    <button
                        onClick={() => setFilter('replied')}
                        className={`px-4 py-2 rounded ${filter === 'replied' ? 'bg-primary text-white' : 'bg-gray-600 text-white'}`}
                    >
                        Replied
                    </button>
                </div>

                {/* Table */}
                <div className="my-10">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#2F2F2F]">
                                <th className="text-left py-4 text-[#666] font-medium">Sender</th>
                                <th className="text-left py-4 text-[#666] font-medium">From Name</th>
                                <th className="text-left py-4 text-[#666] font-medium">Reply To</th>
                                <th className="text-left py-4 text-[#666] font-medium">Message</th>
                                <th className="text-left py-4 text-[#666] font-medium">Date</th>
                                <th className="text-left py-4 text-[#666] font-medium">Status</th>
                                <th className="text-left py-4 text-[#666] font-medium">Replied At</th>
                                <th className="text-left py-4 text-[#666] font-medium">Replied By</th>
                                <th className="text-left py-4 text-[#666] font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedEmails.map((email) => (
                                <tr key={email.id} className="h-[70px] hover:bg-[#2F2F2F] p-2">
                                    <td className="pl-4">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                                                {email.senderName?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-white font-medium">{email.senderName}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-white">{email.from_name}</td>
                                    <td className="text-white">{email.reply_to}</td>
                                    <td className="text-white max-w-xs truncate">{email.message}</td>
                                    <td className="text-white">{formatDate(email.createdAt)}</td>
                                    <td>
                                        <span className={`px-2 py-1 rounded text-xs ${email.replied ? 'bg-green-500' : 'bg-red-500'}`}>
                                            {email.replied ? 'Replied' : 'Pending'}
                                        </span>
                                    </td>
                                    <td className="text-white">
                                        {email.replied_at ? formatDate(email.replied_at) : 'N/A'}
                                    </td>
                                    <td className="text-white">
                                        {email.replier ? email.replier.displayName || email.replier.email || 'Unknown' : 'N/A'}
                                    </td>
                                    <td>
                                        {email.replied ? (
                                            <button
                                                onClick={() => handleView(email)}
                                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                                            >
                                                Show
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleReply(email)}
                                                className="relative px-4 py-2 bg-primary text-white rounded hover:bg-blue-600"
                                            >
                                                Reply
                                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-3 h-3 flex items-center justify-center">
                                                    !
                                                </span>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-auto">
                    <Pagination
                        itemsPerPage={itemsPerPage}
                        totalItems={filteredEmails.length}
                        onPageChange={setCurrentPage}
                    />
                </div>

                {/* Reply Dialog */}
                {showReplyDialog && (
                    <DialogReplyEmail
                        open={showReplyDialog}
                        onClose={() => setShowReplyDialog(false)}
                        emailData={selectedEmail}
                        onReplySent={handleReplySent}
                    />
                )}

                {/* View Reply Dialog */}
                {showViewDialog && (
                    <DialogViewReply
                        open={showViewDialog}
                        onClose={() => setShowViewDialog(false)}
                        email={selectedEmail}
                    />
                )}
            </div>
        </div>
    );
};

export default EmailSupportManagement;